import React, { FC } from 'react'
import { Box } from '@mui/system'
import { Alert } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { DataLoad, EntityHeader, HasSidebarLayout } from 'src/components'
import { PromptForm } from 'src/components/forms/prompt'
import { usePromptSingleRequest, useEditPromptRequest } from 'src/hooks/api'
import { useAddPopupMessage } from 'src/hooks/ui'
import { PromptInput } from 'src/api'
import { Entities } from 'src/enums'

const PromptEdit: FC = () => {
  document.title = 'CLīMIT Admin Panel - Edit prompt'
  const navigate = useNavigate()
  const params = useParams()
  const id = Number(params.id)
  const addMessage = useAddPopupMessage()
  const queryClient = useQueryClient()

  const { isInitialLoading, isError, error, data: prompt } = usePromptSingleRequest({ id })
  const request = useEditPromptRequest({ id })

  const onSubmit = (payload: PromptInput) => {
    request.mutate(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries([Entities.Prompts])
        addMessage({ text: 'Prompt updated successfully', type: 'success' })
        navigate(`/prompts/${id}`)
      },
      onError: ({ message: text }) => addMessage({ text, type: 'error' })
    })
  }

  const locked = (prompt?.responseCount || 0) > 0

  return (
    <HasSidebarLayout>
      <EntityHeader title="Edit prompt" onBack={() => navigate(`/prompts/${id}`)} />
      <Box width="700px" maxWidth="100%" mt={2}>
        <DataLoad isLoading={isInitialLoading} isError={isError} errorMessage={error?.message || ''}>
          {prompt && (
            <>
              {locked && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This prompt already has responses. Title, description, checkbox options and links are locked; you can
                  still update the start date, minimum app version and display options.
                </Alert>
              )}
              <PromptForm
                onSubmit={onSubmit}
                isLoading={request.isLoading}
                submitLabel="Save"
                cancelTo={`/prompts/${id}`}
                locked={locked}
                initialValues={{
                  title: prompt.title,
                  description: prompt.description,
                  minAppVersion: prompt.minAppVersion || '',
                  skippable: prompt.skippable,
                  showNewUsers: prompt.showNewUsers,
                  startDate: prompt.startDate ? new Date(prompt.startDate) : null
                }}
                initialCheckboxOptions={prompt.checkboxOptions}
                initialLinks={prompt.links || []}
              />
            </>
          )}
        </DataLoad>
      </Box>
    </HasSidebarLayout>
  )
}

export default PromptEdit
